import { computed, type ComputedRef } from 'vue';
import { useBillingState } from '@/composables/billing/useBillingState';
import type { Components } from '@/api/generated/api';
import { ProductFeature } from '@/api/generated/api.enum';
import {
  CheckoutOfferSource,
  type CheckoutOfferSource as CheckoutOfferSourceType,
} from '@/services/analytics/checkout';

export function useCheckoutOfferSource(): {
  adminPlansOfferSource: ComputedRef<CheckoutOfferSourceType | null>;
  featureGateOfferSource: ComputedRef<CheckoutOfferSourceType | null>;
  getFeatureGateOfferSource: (feature?: Components.Schemas.ProductFeature | null) => CheckoutOfferSourceType | null;
  upgradeButtonOfferSource: ComputedRef<CheckoutOfferSourceType | null>;
} {
  const { billingStatus, isBaseAfterTrial, isTrialEnded } = useBillingState();

  const isBasisAfterTrial = computed<boolean>(() => isBaseAfterTrial.value || isTrialEnded.value);
  const featureGateOfferSourceByFeature: Partial<Record<Components.Schemas.ProductFeature, CheckoutOfferSourceType>> = {
    [ProductFeature.statistic]: CheckoutOfferSource.BasisFeatureLockStatistic,
    [ProductFeature.report]: CheckoutOfferSource.BasisFeatureLockReport,
    [ProductFeature.collaboration]: CheckoutOfferSource.BasisFeatureLockCollaboration,
    [ProductFeature.tenantCustomization]: CheckoutOfferSource.BasisFeatureLockTenantCustomization,
    [ProductFeature.gradeCustomization]: CheckoutOfferSource.BasisFeatureLockGradeCustomization,
    [ProductFeature.accessRights]: CheckoutOfferSource.BasisFeatureLockAccessRights,
    [ProductFeature.supportMeasures]: CheckoutOfferSource.BasisFeatureLockSupportMeasures,
  };

  const adminPlansOfferSource = computed<CheckoutOfferSourceType | null>(() => {
    if (billingStatus.value?.isTrialActive) return CheckoutOfferSource.TrialAdminPlans;
    if (isBasisAfterTrial.value) return CheckoutOfferSource.BasisAdminPlans;
    return null;
  });

  const getFeatureGateOfferSource = (
    feature?: Components.Schemas.ProductFeature | null,
  ): CheckoutOfferSourceType | null => {
    if (!isBasisAfterTrial.value) return null;
    if (!feature) return CheckoutOfferSource.BasisFeatureLockUnknown;
    return featureGateOfferSourceByFeature[feature] ?? CheckoutOfferSource.BasisFeatureLockUnknown;
  };

  const featureGateOfferSource = computed<CheckoutOfferSourceType | null>(() => getFeatureGateOfferSource());

  const upgradeButtonOfferSource = computed<CheckoutOfferSourceType | null>(() => {
    if (billingStatus.value?.isTrialActive) return CheckoutOfferSource.TrialUpgradeButton;
    if (isBasisAfterTrial.value) return CheckoutOfferSource.BasisUpgradeButton;
    return null;
  });

  return {
    adminPlansOfferSource,
    featureGateOfferSource,
    getFeatureGateOfferSource,
    upgradeButtonOfferSource,
  };
}
